import { useMemo } from 'react';

import {
    QRCodeScreen,
    UserChangePasswordScreen,
    UserEmailConfirmationScreen,
    UserLoginScreen,
    UserRegisterScreen,
    UserResetPasswordScreen,
} from '@auth-v2';
import { ActivityLoader, ParamsLoader, SwingLoader, UserLoader } from '@loaders';
import { Navigate, Outlet, Route, Routes, useParams } from 'react-router';

import { useActivitySwings } from '@queries';
import { ActivityReport, GoalReport, LoadingScreen, PlaybackScreen, UserWelcomeWizardProvider } from '@screens';

import { NavigateToActivitiesButton } from '../components/BackToActivitiesButton/BackToActivitiesButton';
import { BackToActivityButton } from '../components/BackToActivityButton';
import { ModalLauncher } from '../components/ModalLauncher';
import {
    ActivityListScreen,
    ClubSelectionScreen,
    Playback3DScreen,
    SwingDetailsScreen,
    SwingFoundationsEditorScreen,
    TagSelectionScreen,
    UserSettingsScreen,
} from '../screens';
import { LiveAnalysisScreen } from '../screens/LiveAnalysisScreen';
import { StudentSettingsScreen } from '../screens/StudentSettingsScreen';
import { LINKS, PARAMETERS, ROUTES } from '../utils/routes';

import '../styles/fonts.css';
import '../styles/reset.css';

const ActivityRedirect = () => {
    const { activityId } = useParams();
    const { data: activitySwings, isLoading } = useActivitySwings(Number(activityId));
    const firstSwingId = activitySwings?.[0]?.uuid;

    if (isLoading) return <LoadingScreen message="Loading activity..." />;
    return <Navigate to={firstSwingId ? `${PARAMETERS.SWING}/${firstSwingId}` : `${PARAMETERS.LIVE_ANALYSIS}`} />;
};

export const HomeRouter = () => {
    const sharedActivityRoutes = useMemo(
        () => (
            <>
                <Route path={PARAMETERS.CLUBS} element={<ClubSelectionScreen />} />˝
                <Route path={PARAMETERS.TAGS} element={<TagSelectionScreen />} />
                <Route path={PARAMETERS.REPORT} element={<ActivityReport getLinkBack={LINKS.getFullActivity} />} />
                <Route
                    path={`${PARAMETERS.GOAL_REPORT}/${PARAMETERS.LAYOUT_ID}`}
                    element={
                        <GoalReport
                            getFullActivityRoute={LINKS.getFullActivity}
                            backButton={<BackToActivityButton />}
                        />
                    }
                />
                <Route path={PARAMETERS.STUDENT_SETTINGS} element={<StudentSettingsScreen />} />
            </>
        ),
        [],
    );

    return (
        <Routes>
            <Route
                path="/"
                element={
                    <ParamsLoader>
                        <UserLoader>
                            <UserWelcomeWizardProvider>
                                <Outlet />
                            </UserWelcomeWizardProvider>
                        </UserLoader>
                    </ParamsLoader>
                }
            >
                <Route index element={<Navigate to="/activities" />} />
                <Route path={PARAMETERS.USER_SETTINGS} element={<UserSettingsScreen />} />
                <Route path={PARAMETERS.ACTIVITIES}>
                    <Route index element={<ActivityListScreen />} />
                    <Route
                        path={PARAMETERS.ACTIVITY_ID}
                        element={
                            <ActivityLoader errAction={<NavigateToActivitiesButton />}>
                                <Outlet />
                            </ActivityLoader>
                        }
                    >
                        <Route index element={<ActivityRedirect />} />
                        {sharedActivityRoutes}
                        <Route path={PARAMETERS.LIVE_ANALYSIS} element={<LiveAnalysisScreen />} />
                        <Route
                            path={`${PARAMETERS.SWING}/${PARAMETERS.SWING_ID}`}
                            element={
                                <SwingLoader errAction={<NavigateToActivitiesButton />}>
                                    <Outlet />
                                    <ModalLauncher />
                                </SwingLoader>
                            }
                        >
                            <Route index element={<SwingDetailsScreen />} />
                            <Route path={PARAMETERS.PLAYBACK} element={<PlaybackScreen />} />
                            <Route path={PARAMETERS.PARAMETERS} element={<SwingFoundationsEditorScreen />} />
                            <Route path={PARAMETERS.VISUALIZER_3D} element={<Playback3DScreen />} />
                            {sharedActivityRoutes}
                        </Route>
                    </Route>
                </Route>
            </Route>
            <Route
                path={ROUTES.LOGIN}
                element={<UserLoginScreen registerPath={ROUTES.REGISTER} resetPasswordPath={ROUTES.RESET_PASSWORD} />}
            />
            <Route path={ROUTES.REGISTER} element={<UserRegisterScreen confirmationPath={ROUTES.CONFIRM} />} />
            <Route path={ROUTES.CONFIRM} element={<UserEmailConfirmationScreen />} />
            <Route path={ROUTES.RESET_PASSWORD} element={<UserResetPasswordScreen />} />
            <Route path={ROUTES.CHANGE_PASSWORD} element={<UserChangePasswordScreen />} />
            <Route path={ROUTES.QR_CODE} element={<QRCodeScreen redirectPath={ROUTES.LOGIN} />} />
        </Routes>
    );
};
